import { memo } from 'react';
import { parseMessageContent } from '@Utils/actionCards';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ActionCard from './ActionCard';
import ChartCard from './ChartCard';
import type { CardStatus } from './ActionCard';
import type { BusinessObjectMap } from '@Types/soul';

export interface ActionStateEntry {
  status: CardStatus;
  result: string;
  /** For create-ticketed-event — restores the "View event" link after reload. */
  eventId?: string;
}

export interface MessageContentProps {
  message: {
    id: string;
    content: string;
    reasoning?: string;
  };
  businessObjects?: BusinessObjectMap;
  chatId?: string | null;
  messageId?: string;
  actionStates?: Record<string, ActionStateEntry>;
  onActionExecuted?: (
    msg: { id: string; content: string },
    sourceMessageId: string,
    actionIndex: number,
    status: CardStatus,
  ) => void;
}

function MessageContent({
  message,
  businessObjects,
  chatId,
  messageId,
  actionStates = {},
  onActionExecuted,
}: MessageContentProps) {
  const content = message.content;
  const segments = parseMessageContent(content);
  let actionIdx = 0;
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return (
            <div key={i} className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />
                }}
              >
                {seg.content}
              </ReactMarkdown>
            </div>
          );
        }
        const currentActionIndex = actionIdx++;
        const stateKey = messageId ? `${messageId}:${currentActionIndex}` : undefined;
        const persistedState = stateKey && actionStates ? actionStates[stateKey] : undefined;
        if (seg.type === 'chart') {
          return <ChartCard key={i} payload={seg.payload} />;
        }

        return (
          <ActionCard
            key={i}
            payload={seg.payload}
            businessObjects={businessObjects}
            chatId={chatId}
            messageId={messageId}
            actionIndex={currentActionIndex}
            initialStatus={persistedState?.status}
            initialResult={persistedState?.result}
            initialEventId={persistedState?.eventId}
            onActionExecuted={onActionExecuted}
          />
        );
      })}
    </>
  );
}

export default memo(MessageContent);
